import { computed, ref } from 'vue'
import { Preferences } from '@capacitor/preferences'
import { domainFromUrl, fetchOrganization, organizationLogoUrl } from '@/api/olympe'
import type { Organization } from '@/api/olympe'
import { competitionUrls } from '@/data/competitions'
import { toHighlights } from '@/model/highlight'
import type { Highlight } from '@/model/highlight'

/**
 * Store minimal (pattern « composable singleton ») : les `ref` sont déclarés au
 * niveau du module, donc partagés par tous les composants qui importent ce
 * fichier. Pas besoin de Pinia pour un état de cette taille.
 *
 * Il charge le catalogue au démarrage : une requête par URL de
 * `data/competitions.ts`, et c'est la réponse de l'API qui fournit nom, logo,
 * couleur et jeu rattaché. Une compétition qui échoue est simplement absente de
 * la liste (l'app reste utilisable avec les autres).
 *
 * Une seule chose survit au redémarrage : **la compétition choisie**, mémorisée
 * dans `@capacitor/preferences` (jamais `localStorage`, cf. `CLAUDE.md`) et
 * relue au chargement du catalogue. Tout le reste — liste, statut, accent —
 * reste en mémoire et se reconstruit à chaque lancement.
 */

export interface CompetitionGame {
  id: number
  name: string
  /** monogramme de secours, dérivé du nom (le logo prime) */
  short: string
}

export interface Competition {
  /** id de l'organisation Olympe, identifiant de la compétition dans l'app */
  id: number
  name: string
  /** l'URL de la conf : la seule donnée qui ne vient pas de l'API */
  url: string
  logo: string
  /** hex dérivé de la couleur Olympe ; `null` = on garde l'accent du thème */
  accent: string | null
  accentSoft: string | null
  game: CompetitionGame
  /** communications actives, déjà converties */
  highlights: Highlight[]
}

export type CatalogStatus = 'idle' | 'loading' | 'ready' | 'error'

/** Compétitions d'un même jeu — une section du catalogue (onglet « Accueil »). */
export interface GameGroup {
  game: CompetitionGame
  competitions: Competition[]
}

// --- Conversion API → modèle d'app ------------------------------------------

/** L'API rend un nom de couleur ('orange'), le thème veut un hex. */
const PALETTE: Record<string, string> = {
  red: '#d9484a',
  orange: '#f99e1a',
  yellow: '#f5c542',
  green: '#30b45f',
  teal: '#1fb6b0',
  blue: '#0a84ff',
  purple: '#8e5cf7',
  pink: '#ff4f9a',
  gray: '#8e8e93',
  grey: '#8e8e93',
}

/** Version translucide de l'accent, pour les fonds (`--color-accent-soft`). */
function softened(hex: string, alpha = 0.16): string {
  const value = hex.replace('#', '')
  const full = value.length === 3 ? [...value].map((c) => c + c).join('') : value
  const int = Number.parseInt(full, 16)
  return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`
}

/**
 * Hex → teinte (degrés 0-360) et saturation (0-100) HSL.
 * Le thème entier (fond, surfaces, verre, halos du hero) se dérive de ces deux
 * nombres dans `variables.css` : il faut donc les lui passer en nombres, pas en
 * hex. La luminosité, elle, appartient à chaque token.
 */
function hexToHs(hex: string): { h: number; s: number } {
  const value = hex.replace('#', '')
  const full = value.length === 3 ? [...value].map((c) => c + c).join('') : value
  const int = Number.parseInt(full, 16)

  const r = ((int >> 16) & 255) / 255
  const g = ((int >> 8) & 255) / 255
  const b = (int & 255) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  const l = (max + min) / 2

  let h = 0
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta) % 6
    else if (max === g) h = (b - r) / delta + 2
    else h = (r - g) / delta + 4

    h *= 60
    if (h < 0) h += 360
  }

  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

  return { h: Math.round(h), s: Math.round(s * 100) }
}

/** 'League of Legends' → 'LOL', 'Overwatch' → 'OV'. */
function monogram(name: string): string {
  const words = name.split(/[\s-]+/).filter(Boolean)
  const initials = words.length > 1 ? words.map((word) => word[0]).join('') : name.slice(0, 2)
  return initials.slice(0, 3).toUpperCase()
}

function toCompetition(organization: Organization, url: string): Competition {
  const game = organization.game ?? { id: 0, name: organization.name }
  const accent = organization.color ? (PALETTE[organization.color.toLowerCase()] ?? null) : null

  return {
    id: organization.id,
    name: organization.name,
    url,
    logo: organizationLogoUrl(organization.id),
    accent,
    accentSoft: accent ? softened(accent) : null,
    game: { id: game.id, name: game.name, short: monogram(game.name) },
    highlights: toHighlights(organization.communications),
  }
}

// --- État partagé ------------------------------------------------------------

const competitions = ref<Competition[]>([])
const currentId = ref<number | null>(null)
const status = ref<CatalogStatus>('idle')

/** requête en cours, pour ne pas charger le catalogue deux fois */
let pending: Promise<void> | null = null

// --- Sélection mémorisée -----------------------------------------------------

/**
 * La compétition choisie est la **seule** chose que l'app persiste côté
 * catalogue : au relancement, elle rouvre sur l'univers qu'on avait quitté
 * (couleur comprise) plutôt que sur la première URL de `data/competitions.ts`.
 * `@capacitor/preferences`, jamais `localStorage` — c'est l'invariant du
 * projet, et c'est déjà ce que fait `useAuth` pour la session.
 * On ne garde que l'**id** : le reste (nom, logo, couleur) vient de l'API à
 * chaque lancement, donc rien ne peut se retrouver périmé en cache.
 */
const SELECTED_KEY = 'competition.selected'

async function readSelectedId(): Promise<number | null> {
  try {
    const { value } = await Preferences.get({ key: SELECTED_KEY })
    if (!value) return null
    const id = Number(value)
    return Number.isInteger(id) ? id : null
  } catch (reason) {
    // stockage indisponible : on repart sur la première compétition, sans bruit
    console.warn('[olympe] sélection mémorisée illisible', reason)
    return null
  }
}

async function writeSelectedId(id: number): Promise<void> {
  try {
    await Preferences.set({ key: SELECTED_KEY, value: String(id) })
  } catch (reason) {
    console.warn('[olympe] sélection non mémorisée', reason)
  }
}

/** Applique la couleur de la compétition à tout le thème. */
function applyAccent(competition: Competition | null) {
  const root = document.documentElement

  // couleur inconnue : on retire la surcharge pour rendre la main aux tokens
  // de variables.css, qui ont chacun leur variante claire et sombre
  if (!competition?.accent || !competition.accentSoft) {
    root.style.removeProperty('--color-accent')
    root.style.removeProperty('--color-accent-soft')
    root.style.removeProperty('--accent-h')
    root.style.removeProperty('--accent-s')
    return
  }

  const { h, s } = hexToHs(competition.accent)

  root.style.setProperty('--color-accent', competition.accent)
  root.style.setProperty('--color-accent-soft', competition.accentSoft)
  // teinte et saturation : ce sont les SEULES autres couleurs posées en inline,
  // tout le thème teinté (fond, surfaces, séparateurs, verre, hero) s'en dérive
  // dans variables.css
  root.style.setProperty('--accent-h', String(h))
  root.style.setProperty('--accent-s', `${s}%`)
}

function selectCompetition(id: number) {
  if (id === currentId.value) return
  const competition = competitions.value.find((c) => c.id === id)
  if (!competition) return
  currentId.value = competition.id
  applyAccent(competition)
  // pas d'await : le rendu ne dépend pas de l'écriture, et un échec est avalé
  void writeSelectedId(competition.id)
}

async function resolve(url: string): Promise<Competition> {
  const domain = domainFromUrl(url)
  if (!domain) throw new Error(`URL de compétition invalide : « ${url} »`)
  return toCompetition(await fetchOrganization(domain), url)
}

/** Charge le catalogue. Idempotent : les appels concurrents partagent la requête. */
function loadCompetitions(): Promise<void> {
  if (pending) return pending

  status.value = 'loading'
  // la sélection mémorisée se lit EN PARALLÈLE du réseau : elle ne peut de
  // toute façon être appliquée qu'une fois la liste là (il faut vérifier que
  // l'id existe encore), et elle ne doit pas retarder le premier rendu.
  pending = Promise.all([Promise.allSettled(competitionUrls.map(resolve)), readSelectedId()])
    .then(([results, savedId]) => {
      for (const result of results) {
        if (result.status === 'rejected') console.warn('[olympe] compétition ignorée', result.reason)
      }

      competitions.value = results.flatMap((result) =>
        result.status === 'fulfilled' ? [result.value] : [],
      )
      status.value = competitions.value.length > 0 ? 'ready' : 'error'

      // `currentId` non nul = l'utilisateur a déjà choisi pendant le chargement :
      // son geste prime sur la mémoire. Sinon on restaure, et on retombe sur la
      // première compétition si l'id mémorisé n'est plus dans le catalogue
      // (URL retirée de `data/competitions.ts`, ou organisation en échec).
      if (currentId.value !== null) return

      const restored =
        savedId === null ? undefined : competitions.value.find((c) => c.id === savedId)
      const target = restored ?? competitions.value[0]
      if (target) selectCompetition(target.id)
    })
    .finally(() => {
      // libéré dans tous les cas : un échec doit pouvoir être retenté
      pending = null
    })

  return pending
}

export function useCompetition() {
  const currentCompetition = computed<Competition | null>(
    () => competitions.value.find((c) => c.id === currentId.value) ?? null,
  )

  /** Groupé par jeu, dans l'ordre d'arrivée des compétitions. */
  const gameGroups = computed<GameGroup[]>(() => {
    const groups = new Map<number, GameGroup>()

    for (const competition of competitions.value) {
      const group = groups.get(competition.game.id)
      if (group) group.competitions.push(competition)
      else groups.set(competition.game.id, { game: competition.game, competitions: [competition] })
    }

    return [...groups.values()]
  })

  return {
    competitions,
    gameGroups,
    currentCompetition,
    status,
    loadCompetitions,
    selectCompetition,
  }
}
